import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Euro, RefreshCw, Search, Loader2, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  paid: { label: 'Pagado', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  unpaid: { label: 'Pendiente', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  initiated: { label: 'Pendiente', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  expired: { label: 'Caducado', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const TYPE_LABELS = {
  registration: 'Registro',
  book_order: 'Pedido',
  contributor_purchase: 'Colaborador',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const AdminPaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments`, { withCredentials: true });
      setPayments(res.data.payments || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter(p =>
      (p.payer_name || '').toLowerCase().includes(q) ||
      (p.payer_email || '').toLowerCase().includes(q) ||
      (p.concept || '').toLowerCase().includes(q) ||
      (TYPE_LABELS[p.type] || '').toLowerCase().includes(q)
    );
  }, [payments, search]);

  const paid = payments.filter(p => p.payment_status === 'paid');
  const paidTotal = paid.reduce((sum, p) => sum + (p.amount_eur || 0), 0);
  const pendingCount = payments.filter(p => ['unpaid', 'initiated'].includes(p.payment_status)).length;

  return (
    <Card className="card-elevated" data-testid="admin-payments-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Pagos de Stripe
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Transacciones de la pasarela: registros, pedidos y compras de colaboradores
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" data-testid="payments-paid-summary">
              <Euro className="w-3 h-3" />
              {paid.length} pagados · {paidTotal.toFixed(2)}€
            </Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="payments-pending-summary">
              {pendingCount} pendientes
            </Badge>
            <Button size="sm" variant="outline" className="h-8 gap-1" onClick={fetchPayments} data-testid="payments-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
              Actualizar
            </Button>
          </div>
        </div>
        <div className="relative mt-3 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o concepto..."
            className="pl-9 h-9"
            data-testid="payments-search-input"
          />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="payments-empty">
            {payments.length === 0
              ? 'Todavía no hay transacciones. Aparecerán aquí en cuanto alguien inicie un pago.'
              : 'Sin resultados para esa búsqueda.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="payments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Concepto</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const status = STATUS_STYLES[p.payment_status] || { label: p.payment_status, className: 'bg-gray-100 text-gray-600' };
                  return (
                    <TableRow key={p.session_id} data-testid={`payment-row-${p.session_id}`}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatDate(p.paid_at || p.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.payer_name || '—'}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.payer_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm max-w-[280px] truncate" title={p.concept}>{p.concept}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[11px]">{TYPE_LABELS[p.type] || p.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[11px] ${status.className}`}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold whitespace-nowrap">
                        {(p.amount_eur ?? 0).toFixed(2)}€
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
